import React from 'react';
import { Card, CardContent, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Chip, LinearProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { t } from '../../../utils/textUtils';
import { formatPercentage, extractRomanNumeral, formatDate, computeTotalProgressPercent } from '../utils/formatters';

const TaskPerKategoriTable = ({ 
  isProjectMode, 
  isUnitMode, 
  projectList,
  bangunanList,
  categoryList,
  selectedProject,
  selectedBangunan,
  selectedCategory,
  dataByCategory,
  unitOverviewData,
  categoryTasks,
  onDownloadKategoriExcel,
  onDownloadKategoriPDF
}) => {
  return (
    <Card 
      sx={{ 
        border: '1px solid #E2E8F0', 
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        overflow: 'hidden'
      }}
    >
      {/* Card Header */}
      <Box sx={{
        px: 3, py: 2,
        borderBottom: '1px solid #F1F5F9',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: '#FAFBFC'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 2,
            background: 'linear-gradient(135deg, #7C3AED, #A78BFA)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(124,58,237,0.3)'
          }}>
            <ListAltIcon sx={{ color: '#fff', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, lineHeight: 1.2 }}>
              {isProjectMode ? t('PROGRESS KATEGORI') : t('PROGRESS TASK PER KATEGORI')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
              {isProjectMode
                ? `${projectList.find(p => p.id === selectedProject)?.name || t('PROJECT TERPILIH')} - ${t('SEMUA KATEGORI')}`
                : isUnitMode 
                  ? `${bangunanList.find(b => b.id === selectedBangunan)?.name || t('UNIT BANGUNAN TERPILIH')} - ${t('SEMUA KATEGORI')}`
                  : categoryList.find(c => c.id === selectedCategory)?.name || t('KATEGORI TERPILIH')
              }
            </Typography>
          </Box>
        </Box>
        {isUnitMode && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                sx={{
                  borderColor: '#22C55E',
                  color: '#22C55E',
                  '&:hover': {
                    borderColor: '#16A34A',
                    bgcolor: '#F0FDF4'
                  }
                }}
                startIcon={<DownloadIcon />}
                onClick={onDownloadKategoriExcel}
              >
                Excel
              </Button>
              <Button
                size="small"
                variant="outlined"
                sx={{
                  borderColor: '#EF4444',
                  color: '#EF4444',
                  '&:hover': {
                    borderColor: '#DC2626',
                    bgcolor: '#FEF2F2'
                  }
                }}
                startIcon={<DownloadIcon />}
                onClick={onDownloadKategoriPDF}
              >
                PDF
              </Button>
            </Box>
          )}
      </Box>
      <CardContent sx={{ p: 3 }}>
        {isProjectMode ? (
          (dataByCategory && dataByCategory.length > 0) ? (
            <TableContainer sx={{ border: '1px solid #E2E8F0', borderRadius: 1 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>{t('KATEGORI')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} colSpan={3} align="center">{t('PROGRESS')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>{t('TANGGAL MULAI')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>{t('TANGGAL SELESAI')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>{t('DURASI')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#64748B' }} align="center">{t('PERIODE LALU')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#64748B' }} align="center">{t('PERIODE INI')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#64748B' }} align="center">{t('TOTAL')}</TableCell>
                    <TableCell colSpan={3}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...dataByCategory]
                    .sort((a, b) => extractRomanNumeral(a.workCategoryName) - extractRomanNumeral(b.workCategoryName))
                    .map((row, index) => {
                      const progressTotal = computeTotalProgressPercent(row);
                      const progressBefore = row.quantity ? (parseFloat(row.progressBeforeThisWeekVolume) || 0) / parseFloat(row.quantity) * 100 : 0;
                      const progressThisWeek = row.quantity ? (parseFloat(row.progressThisWeekVolume) || 0) / parseFloat(row.quantity) * 100 : 0;
                      return (
                        <TableRow 
                          key={`${row.workCategoryId}-${row.modularUnitId}-${index}`}
                          sx={{ 
                            '&:hover': { bgcolor: '#F8FAFC' },
                            '&:last-child td': { border: 0 }
                          }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                              {row.workCategoryName}
                            </Typography>
                            {row.modularUnitName && (
                              <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                                {row.modularUnitName}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 2 }}>
                            <Chip
                              label={formatPercentage(progressBefore)}
                              size="small"
                              sx={{
                                minWidth: 60,
                                bgcolor: '#F1F5F9',
                                color: '#64748B',
                                fontWeight: 600
                              }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 2 }}>
                            <Chip
                              label={formatPercentage(progressThisWeek)}
                              size="small"
                              sx={{
                                minWidth: 60,
                                bgcolor: '#DBEAFE',
                                color: '#1D4ED8',
                                fontWeight: 600
                              }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 2 }}>
                            <Chip
                              label={formatPercentage(progressTotal)}
                              size="small"
                              sx={{
                                minWidth: 60,
                                fontWeight: 600,
                                bgcolor: progressTotal >= 80 ? '#DCFCE7' : progressTotal >= 50 ? '#FEF3C7' : '#FEE2E2',
                                color: progressTotal >= 80 ? '#166534' : progressTotal >= 50 ? '#92400E' : '#991B1B'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" color="#64748B">
                              {formatDate(row.startDate)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" color="#64748B">
                              {formatDate(row.endDate)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" color="#64748B">
                              {row.durationDays ? `${row.durationDays} ${t('HARI')}` : '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              bgcolor: '#F8FAFC',
              borderRadius: 1
            }}>
              <Typography variant="body2" color="#64748B" sx={{ fontWeight: 500 }}>
                {t('TIDAK ADA KATEGORI DALAM PROJECT INI')}
              </Typography>
            </Box>
          )
        ) : isUnitMode ? (
          unitOverviewData.tasksPerKategori.length > 0 ? (
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {unitOverviewData.tasksPerKategori.map((categoryGroup, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    color: '#0f172a', 
                    mb: 1,
                    borderBottom: '1px solid #E2E8F0',
                    pb: 0.5
                  }}>
                    📁 {categoryGroup.kategori}
                  </Typography>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1,
                    mb: 2,
                    p: 1.5,
                    backgroundColor: '#F8FAFC',
                    borderRadius: 1,
                    border: '1px solid #E2E8F0'
                  }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                        {t('PERIODE LALU')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                        {formatPercentage(categoryGroup.progressPeriodeLalu || 0)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                        {t('PERIODE INI')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1D4ED8' }}>
                        {formatPercentage(categoryGroup.progressPeriodeIni || 0)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                        {t('PROGRESS TOTAL')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#22C55E' }}>
                        {formatPercentage(categoryGroup.progressTotal || 0)}
                      </Typography>
                    </Box>
                  </Box>
                  <TableContainer sx={{ border: '1px solid #E2E8F0', borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, color: '#475569' }}>
                            {t('TASK NAME')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#475569', width: 120 }} align="center">
                            {t('PERIODE LALU')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#475569', width: 120 }} align="center">
                            {t('PERIODE INI')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#475569', width: 150 }}>
                            {t('PROGRESS TOTAL')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#475569', width: 100 }}>
                            {t('START DATE')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#475569', width: 100 }}>
                            {t('END DATE')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#475569', width: 80 }}>
                            {t('DURATION')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categoryGroup.task.map((task, index) => (
                          <TableRow 
                            key={`${task.id}-${index}`}
                            sx={{ 
                              '&:hover': { bgcolor: '#F8FAFC' },
                              '&:last-child td': { border: 0 }
                            }}
                          >
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                                {task.name}
                              </Typography>
                              {task.description && (
                                <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                                  {task.description.length > 50 
                                    ? task.description.substring(0, 50) + '...' 
                                    : task.description
                                  }
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Chip 
                                label={formatPercentage(task.progressPeriodeLalu || 0)}
                                size="small"
                                sx={{ minWidth: 72, bgcolor: '#F1F5F9', color: '#64748B', fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Chip 
                                label={formatPercentage(task.progressPeriodeIni || 0)}
                                size="small"
                                sx={{ minWidth: 72, bgcolor: '#DBEAFE', color: '#1D4ED8', fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={task.progressTotal || 0}
                                  sx={{
                                    flexGrow: 1,
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: '#E2E8F0',
                                    '& .MuiLinearProgress-bar': {
                                      backgroundColor: (task.progressTotal || 0) >= 80 ? '#22C55E' : 
                                                     (task.progressTotal || 0) >= 50 ? '#F59E0B' : '#EF4444',
                                      borderRadius: 4
                                    }
                                  }}
                                />
                                <Typography variant="caption" sx={{ minWidth: 35, textAlign: 'right', fontWeight: 600 }}>
                                  {formatPercentage(task.progressTotal || 0)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="caption" color="#64748B">
                                {formatDate(task.startDate)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="caption" color="#64748B">
                                {formatDate(task.endDate)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="caption" color="#64748B">
                                {task.duration ? `${task.duration} ${t('HARI')}` : '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              bgcolor: '#F8FAFC',
              borderRadius: 1
            }}>
              <Typography variant="body2" color="#64748B" sx={{ fontWeight: 500 }}>
                📝 {t('TIDAK ADA TASK DALAM UNIT BANGUNAN INI')}
              </Typography>
            </Box>
          )
        ) : (
          categoryTasks.length > 0 ? (
            <TableContainer sx={{ maxHeight: 400, border: '1px solid #E2E8F0', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>
                      {t('TASK NAME')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', width: 120 }}>
                      {t('PROGRESS TOTAL')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', width: 100 }}>
                      {t('START DATE')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', width: 100 }}>
                      {t('END DATE')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', width: 80 }}>
                      {t('DURATION')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categoryTasks.map((task, index) => (
                    <TableRow 
                      key={`${task.id}-2-${index}`}
                      sx={{ 
                        '&:hover': { bgcolor: '#F8FAFC' },
                        '&:last-child td': { border: 0 }
                      }}
                    >
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                          {task.name}
                        </Typography>
                        {task.description && (
                          <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                            {task.description.length > 50 
                              ? task.description.substring(0, 50) + '...' 
                              : task.description
                            }
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={task.progress || 0}
                            sx={{
                              flexGrow: 1,
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: '#E2E8F0',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: (task.progress || 0) >= 80 ? '#22C55E' : 
                                               (task.progress || 0) >= 50 ? '#F59E0B' : '#EF4444',
                                borderRadius: 4
                              }
                            }}
                          />
                          <Typography variant="caption" sx={{ minWidth: 35, textAlign: 'right', fontWeight: 600 }}>
                            {task.progress || 0}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="caption" color="#64748B">
                          {task.startDate ? formatDate(task.startDate) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="caption" color="#64748B">
                          {task.endDate ? formatDate(task.endDate) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="caption" color="#64748B">
                          {task.duration ? `${task.duration} ${t('HARI')}` : '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              bgcolor: '#F8FAFC',
              borderRadius: 1
            }}>
              <Typography variant="body2" color="#64748B" sx={{ fontWeight: 500 }}>
                {t('TIDAK ADA TASK DALAM KATEGORI INI')}
              </Typography>
            </Box>
          )
        )}
      </CardContent>
    </Card>
  );
};

export default TaskPerKategoriTable;
